/* eslint-disable no-console */
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
} from 'react';
import {
  POQueryParam,
  POSearchFilter,
  UpdatedPOInformation,
} from '../../types/types';
import { useLocation } from 'react-router-dom';
import {
  REDIRECT_ACTION,
  REDIRECT_ORIGIN,
  REDIRECT_PARAM_VALUE_KEY,
} from '../../constants/constants';

export const initialFilter = {
  poNumber: '',
  fromDate: '',
  toDate: '',
};

export const initialQueryParam = {
  poNumber: '',
  storeActionItemId: '',
  src: '',
};

export interface SearchState {
  filter: POSearchFilter;
  searchedPOInSection: string;
  showLoader: boolean;
  manualPOLoading: boolean;
  regularPOLoading: boolean;
  previouslyReceivedPOLoading: boolean;
  closedCancelledPOLoading: boolean;
  noSearchItemFound: boolean;
  searchItemFound: boolean;
  manualPOReceive: string;
  reloadData: boolean;
  queryParamsProp: POQueryParam;
  bol: string;
  partialReceivedPOs: any;
  enableMXPOReceivingValue: string;
  supportContactNumber: string;
  oracleFusionEnableValue: string;
  localSourceRampValue: string;
}

export interface SearchDispatchState {
  setSearchFilter: (poNumber: string, fromDate: string, toDate: string) => void;
  setShowLoader: (value: boolean) => void;
  setSearchedPOInSection: (value: string) => void;
  setManualPOLoading: (value: boolean) => void;
  setRegularPOLoading: (value: boolean) => void;
  setPreviouslyReceivedPOLoading: (value: boolean) => void;
  setClosedCancelledPOLoading: (value: boolean) => void;
  setNoSearchItemFound: (value: boolean) => void;
  clearFilter: () => void;
  setSearchItemFound: (value: boolean) => void;
  setReloadData: (value: boolean) => void;
  setManualPOReceive: (value: string) => void;
  setBol: (value: string) => void;
  setPartialReceivedPOs: any;
  setEnableMXPOReceivingValue: (value: string) => void;
  setSupportContactNumber: (value: string) => void;
  setOracleFusionEnableValue: (value: string) => void;
  setLocalSourceRampValue: (value: string) => void;
}

export const SearchStateContext = createContext<SearchState>({} as SearchState);
export const SearchDispatchContext = createContext<SearchDispatchState>(
  {} as SearchDispatchState
);

const getPoNumberFromUrl = (search: string) => {
  const origin = new URLSearchParams(search).get('origin');
  const action = new URLSearchParams(search).get('action');
  const storeActionItemId = new URLSearchParams(search).get('saiid');
  const customerId = new URLSearchParams(search).get('custid');
  const agreementId = new URLSearchParams(search).get('agrid');
  const inventoryId =
    new URLSearchParams(search).get('inventoryId') || undefined;
  const poNumber = new URLSearchParams(search).get(
    REDIRECT_PARAM_VALUE_KEY.PO_NUMBER
  );
  const isLandedManualPOList =
    origin === REDIRECT_ORIGIN.MANUAL_PO_LIST &&
    action === REDIRECT_ACTION.RECEIVE_PO;
  REDIRECT_PARAM_VALUE_KEY;

  if (origin == 'TB' && storeActionItemId && poNumber) {
    return {
      origin,
      storeActionItemId,
      poNumber,
      agreementId,
      customerId,
    };
  } else if (origin === REDIRECT_ORIGIN.INVENTORY && poNumber) {
    return {
      origin,
      poNumber,
      inventoryId,
    };
  } else if (isLandedManualPOList && poNumber) {
    return {
      poNumber,
    };
  } else {
    return {
      poNumber: '',
    };
  }
  // return isLandedManualPOList && poNumber ? poNumber : '';
};

export const SearchProvider = (props: { children: ReactNode }) => {
  const location = useLocation();
  const search = location.search;
  const poAndQueryParamDetails = getPoNumberFromUrl(search);

  const [manualPOReceive, setManualPOReceive] = useState(
    poAndQueryParamDetails?.poNumber
  );
  const [filter, setFilter] = useState<POSearchFilter>(initialFilter);
  const [noSearchItemFound, setNoSearchItemFound] = useState(false);
  const [searchItemFound, setSearchItemFound] = useState(false);
  const [manualPOLoading, setManualPOLoading] = useState(false);
  const [regularPOLoading, setRegularPOLoading] = useState(false);
  const [previouslyReceivedPOLoading, setPreviouslyReceivedPOLoading] =
    useState(false);
  const [closedCancelledPOLoading, setClosedCancelledPOLoading] =
    useState(false);
  const [searchedPOInSection, setSearchedPOInSection] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [partialReceivedPOs, setPartialReceivedPOs] = useState<
    UpdatedPOInformation[]
  >([]);
  const [enableMXPOReceivingValue, setEnableMXPOReceivingValue] =
    useState<string>('');
  const [supportContactNumber, setSupportContactNumber] = useState<string>('');
  const [oracleFusionEnableValue, setOracleFusionEnableValue] =
    useState<string>('');
  const [localSourceRampValue, setLocalSourceRampValue] = useState<string>('');

  const [bol, setBol] = useState('');

  const queryParamsProp: POQueryParam = {
    origin: poAndQueryParamDetails?.origin || '',
    poNumber: poAndQueryParamDetails?.poNumber || '',
    storeActionItemId: poAndQueryParamDetails?.storeActionItemId || '',
    agreementId: poAndQueryParamDetails?.agreementId || '',
    customerId: poAndQueryParamDetails?.customerId || '',
    inventoryId: poAndQueryParamDetails?.inventoryId || '',
  };

  const setSearchFilter = (
    poNumber: string,
    fromDate: string,
    toDate: string
  ) => {
    setSearchItemFound(false);
    setFilter({
      poNumber,
      fromDate,
      toDate,
    });
  };

  useEffect(() => {
    filter.poNumber && setShowLoader(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.poNumber, filter.fromDate, filter.toDate]);

  const clearFilter = () => setFilter(initialFilter);

  useEffect(() => {
    const allDataLoaded =
      !manualPOLoading &&
      !previouslyReceivedPOLoading &&
      !regularPOLoading &&
      !closedCancelledPOLoading;
    if (filter.poNumber && allDataLoaded) {
      setShowLoader(false);
    }

    if (filter.poNumber && allDataLoaded && !searchItemFound && !showLoader) {
      setNoSearchItemFound(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    manualPOLoading,
    previouslyReceivedPOLoading,
    closedCancelledPOLoading,
    regularPOLoading,
    searchedPOInSection,
  ]);

  useEffect(() => {
    const allDataLoaded =
      !manualPOLoading &&
      !previouslyReceivedPOLoading &&
      !closedCancelledPOLoading &&
      !regularPOLoading;

    if (filter.poNumber && allDataLoaded && !searchItemFound && !showLoader) {
      setNoSearchItemFound(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    manualPOLoading,
    previouslyReceivedPOLoading,
    closedCancelledPOLoading,
    regularPOLoading,
    showLoader,
  ]);

  useEffect(() => {
    if (
      reloadData &&
      manualPOLoading &&
      previouslyReceivedPOLoading &&
      closedCancelledPOLoading &&
      regularPOLoading
    ) {
      setReloadData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    manualPOLoading,
    previouslyReceivedPOLoading,
    closedCancelledPOLoading,
    regularPOLoading,
    reloadData,
  ]);

  return (
    <SearchStateContext.Provider
      value={{
        filter,
        searchedPOInSection,
        showLoader,
        manualPOLoading,
        regularPOLoading,
        previouslyReceivedPOLoading,
        closedCancelledPOLoading,
        noSearchItemFound,
        searchItemFound,
        manualPOReceive,
        reloadData,
        queryParamsProp,
        bol,
        partialReceivedPOs,
        enableMXPOReceivingValue,
        supportContactNumber,
        oracleFusionEnableValue,
        localSourceRampValue,
      }}
    >
      <SearchDispatchContext.Provider
        value={{
          setSearchFilter,
          setShowLoader,
          setSearchedPOInSection,
          clearFilter,
          setManualPOLoading,
          setRegularPOLoading,
          setPreviouslyReceivedPOLoading,
          setClosedCancelledPOLoading,
          setNoSearchItemFound,
          setSearchItemFound,
          setReloadData,
          setManualPOReceive,
          setBol,
          setPartialReceivedPOs,
          setEnableMXPOReceivingValue,
          setSupportContactNumber,
          setOracleFusionEnableValue,
          setLocalSourceRampValue,
        }}
      >
        {props.children}
      </SearchDispatchContext.Provider>
    </SearchStateContext.Provider>
  );
};

export const useSearchDetails = () => useContext(SearchStateContext);

export const useSearchActions = () => useContext(SearchDispatchContext);
